import { Entity } from './Entity';
import { BOSS_TYPES, BOSS_PHASES, BossPhase } from '../config/bosses';

export class Boss implements Entity {
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public active: boolean = true;

  private hp: number;
  private maxHp: number;
  private speed: number;
  private score: number;
  private color: string;
  private type: string;
  private name: string;
  
  // Boss state
  private currentPhase: number = 0;
  private enraged: boolean = false;
  private moveTimer: number = 0;
  private attackTimer: number = 0;
  private phaseConfig: BossPhase[];

  constructor(x: number, y: number, type: string) {
    const config = BOSS_TYPES[type.toUpperCase()] || BOSS_TYPES.GIANT_HUNGER_WORM;
    
    this.x = x;
    this.y = y;
    this.width = config.size;
    this.height = config.size;
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.speed = config.speed;
    this.score = config.score;
    this.color = config.color;
    this.type = config.type;
    this.name = config.name;
    this.phaseConfig = BOSS_PHASES[config.type] || [];
  }

  public update(deltaTime: number): void {
    this.moveTimer += deltaTime;
    this.attackTimer += deltaTime;

    // Check for phase transition based on HP
    const hpPercent = this.hp / this.maxHp;
    const newPhase = Math.floor((1 - hpPercent) * this.phaseConfig.length);
    if (newPhase !== this.currentPhase && newPhase < this.phaseConfig.length) {
      this.currentPhase = newPhase;
      console.log(`Boss entered phase ${this.currentPhase + 1}`);
    }

    // Check for enrage
    if (!this.enraged && hpPercent < 0.33) {
      this.enraged = true;
      this.speed *= 2;
      console.log('Boss ENRAGED!');
    }

    // Update movement based on current phase
    this.updateMovement(deltaTime);
  }

  private updateMovement(deltaTime: number): void {
    if (this.phaseConfig.length === 0) return;

    const phase = this.phaseConfig[this.currentPhase];
    const pattern = phase?.movePattern || 'horizontal_sweep';

    switch (pattern) {
      case 'horizontal_sweep':
        this.x += Math.sin(this.moveTimer * 2) * this.speed * 100 * deltaTime;
        this.x = Math.max(this.width, Math.min(480 - this.width, this.x));
        break;
      
      case 'figure_eight':
        this.x = 240 + Math.sin(this.moveTimer * 2) * 150;
        this.y = 150 + Math.sin(this.moveTimer * 4) * 50;
        break;
      
      case 'aggressive':
        this.y += Math.sin(this.moveTimer * 3) * this.speed * 50 * deltaTime;
        this.y = Math.max(100, Math.min(300, this.y));
        break;
      
      case 'stationary':
        // Minimal movement
        this.x += Math.sin(this.moveTimer) * this.speed * 20 * deltaTime;
        break;
      
      case 'teleport':
        // Shadow King teleport pattern
        if (Math.floor(this.moveTimer * 2) % 3 === 0) {
          // Occasional random teleport
          if (Math.random() < 0.01) {
            this.x = 100 + Math.random() * 280;
            this.y = 100 + Math.random() * 100;
          }
        }
        break;
      
      case 'circle':
        const centerX = 240;
        const centerY = 150;
        const radius = 100;
        this.x = centerX + Math.cos(this.moveTimer * 2) * radius;
        this.y = centerY + Math.sin(this.moveTimer * 2) * radius;
        break;
      
      default:
        this.x += Math.sin(this.moveTimer) * this.speed * 80 * deltaTime;
    }
  }

  public takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.active = false;
      return true; // Boss defeated
    }
    return false;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.x, this.y);

    // Flash red when hit
    if (this.enraged) {
      ctx.shadowBlur = 20;
      ctx.shadowColor = this.color;
    }

    // Draw boss based on type
    ctx.fillStyle = this.color;
    
    switch (this.type) {
      case 'giant_hunger_worm':
        this.renderHungerWorm(ctx);
        break;
      case 'shadow_king':
        this.renderShadowKing(ctx);
        break;
      case 'greed_machine':
        this.renderGreedMachine(ctx);
        break;
      case 'storm_titan':
        this.renderStormTitan(ctx);
        break;
      case 'capitol_guardian':
        this.renderCapitolGuardian(ctx);
        break;
      default:
        // Default large enemy
        ctx.beginPath();
        ctx.arc(0, 0, this.width / 2, 0, Math.PI * 2);
        ctx.fill();
    }

    // HP bar
    const barWidth = this.width * 1.2;
    const barHeight = 8;
    const hpPercent = this.hp / this.maxHp;
    
    ctx.fillStyle = '#330000';
    ctx.fillRect(-barWidth / 2, -this.height / 2 - 20, barWidth, barHeight);
    
    ctx.fillStyle = hpPercent > 0.33 ? '#00ff00' : '#ff0000';
    ctx.fillRect(-barWidth / 2, -this.height / 2 - 20, barWidth * hpPercent, barHeight);
    
    // Boss name
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(this.name, 0, -this.height / 2 - 30);

    ctx.restore();
  }

  private renderHungerWorm(ctx: CanvasRenderingContext2D): void {
    // Segmented worm
    for (let i = 0; i < 5; i++) {
      const offset = Math.sin(this.moveTimer * 5 + i) * 10;
      ctx.beginPath();
      ctx.arc(offset, i * 15 - 30, this.width / 5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderShadowKing(ctx: CanvasRenderingContext2D): void {
    // Ethereal shadow figure
    const alpha = 0.5 + Math.sin(this.moveTimer * 5) * 0.3;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(0, 0, this.width / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Crown
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ffff00';
    for (let i = 0; i < 5; i++) {
      ctx.fillRect(-20 + i * 10, -this.height / 2, 6, 15);
    }
  }

  private renderGreedMachine(ctx: CanvasRenderingContext2D): void {
    // Mechanical construct
    ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    
    // Dollar signs
    ctx.fillStyle = '#ffff00';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('$$$', 0, 0);
  }

  private renderStormTitan(ctx: CanvasRenderingContext2D): void {
    // Storm cloud giant
    ctx.globalAlpha = 0.8;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.arc(
        (i - 1.5) * 20,
        Math.sin(this.moveTimer * 3 + i) * 10,
        this.width / 4,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    
    // Lightning bolts
    if (Math.floor(this.moveTimer * 3) % 2 === 0) {
      ctx.strokeStyle = '#ffff00';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(-10, this.height / 2);
      ctx.lineTo(-15, this.height / 2 + 30);
      ctx.lineTo(-5, this.height / 2 + 30);
      ctx.stroke();
    }
  }

  private renderCapitolGuardian(ctx: CanvasRenderingContext2D): void {
    // Statue/mech
    ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    
    // Rotating shield
    ctx.save();
    ctx.rotate(this.moveTimer * 3);
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, 0, this.width / 2 + 10, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  public getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - this.width / 2,
      y: this.y - this.height / 2,
      width: this.width,
      height: this.height,
    };
  }

  public getScore(): number {
    return this.score;
  }

  public getType(): string {
    return this.type;
  }

  public getName(): string {
    return this.name;
  }

  public getCurrentPhase(): number {
    return this.currentPhase;
  }

  public isEnraged(): boolean {
    return this.enraged;
  }

  public canAttack(): boolean {
    if (this.phaseConfig.length === 0) return false;
    const phase = this.phaseConfig[this.currentPhase];
    const fireRate = phase?.fireRate || 1;
    return this.attackTimer >= 1 / fireRate;
  }

  public resetAttackTimer(): void {
    this.attackTimer = 0;
  }
}
